import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import isUndefined from 'lodash/isUndefined';
import { FALLBACK_SIDEBAR_RESPONSE } from './constants';
import { getServerUrl } from '../../../utils/getServerUrl';
import { useQueryEntitlements } from '../../../hooks/useQueryEntitlements';
import { useQueryAccountRestriction } from '../../../hooks/useQueryAccountRestriction';
import { useQuerySession } from '../../../hooks/useQuerySession';
import { useUniversalNavContext } from '../../../hooks';
export const useQuerySidebar = () => {
    var _a, _b, _c, _d;
    const { data: sessionData } = useQuerySession();
    const { bffHostname } = useUniversalNavContext();
    const currentAccountId = ((_a = sessionData === null || sessionData === void 0 ? void 0 : sessionData.currentAccountId) === null || _a === void 0 ? void 0 : _a.toString()) || '';
    const isStaff = ((_b = sessionData === null || sessionData === void 0 ? void 0 : sessionData.isStaff) === null || _b === void 0 ? void 0 : _b.toString()) || '';
    const userId = ((_c = sessionData === null || sessionData === void 0 ? void 0 : sessionData.customUserId) === null || _c === void 0 ? void 0 : _c.toString()) || '';
    const currentRole = (_d = sessionData === null || sessionData === void 0 ? void 0 : sessionData.currentUserRole) !== null && _d !== void 0 ? _d : '';
    const { data: entitlements } = useQueryEntitlements(sessionData === null || sessionData === void 0 ? void 0 : sessionData.currentAccountId);
    const { data: accountRestrictionData } = useQueryAccountRestriction(sessionData === null || sessionData === void 0 ? void 0 : sessionData.currentAccountId);
    const isSidebarQueryDisabled = useMemo(() => isUndefined(sessionData) ||
        isUndefined(entitlements) ||
        isUndefined(accountRestrictionData), [sessionData, entitlements, accountRestrictionData]);
    const result = useQuery({
        queryKey: [
            'sidebar',
            sessionData,
            entitlements,
            accountRestrictionData,
        ],
        queryFn: async () => {
            const serverUrl = getServerUrl(bffHostname);
            const sidebarUrl = new URL('/api/v3/navigation-data/sidebar', serverUrl || window.location.origin);
            if (entitlements) {
                sidebarUrl.searchParams.append('is_alerts_center_enabled', entitlements.limits.isAlertsCenterEnabled ? 'true' : 'false');
                sidebarUrl.searchParams.append('is_analytics_dashboard_enabled', entitlements.limits.isAnalyticsDashboardEnabled ? 'true' : 'false');
                const isApprovalRequestsEnabled = entitlements.limits.isApprovalRequestsEnabled &&
                    (accountRestrictionData === null || accountRestrictionData === void 0 ? void 0 : accountRestrictionData.approval_required);
                sidebarUrl.searchParams.append('is_approval_requests_enabled', isApprovalRequestsEnabled ? 'true' : 'false');
            }
            const response = await fetch(sidebarUrl.toString(), {
                credentials: 'include',
                headers: {
                    'x-is-staff': isStaff,
                    'x-current-account-id': currentAccountId,
                    'x-current-role': currentRole,
                    'x-customuser-id': userId,
                },
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const json = await response.json();
            return json.data;
        },
        placeholderData: FALLBACK_SIDEBAR_RESPONSE,
        enabled: !isSidebarQueryDisabled,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 60,
    });
    const data = result.isLoading || result.isError
        ? FALLBACK_SIDEBAR_RESPONSE
        : result.data;
    return Object.assign(Object.assign({}, result), { data });
};
//# sourceMappingURL=useQuerySidebar.js.map
// @zapier/universal-layout@12.0.37 (fb08e5a7f)
